/**
 * Storage overlay -- buckets from RDS (Phase E, TEST_INTEGRATION_PLAN.md §29).
 *
 * The object-store counterpart of rdsPhiOverlay.js: installed ONCE on the
 * Supabase clients that components/supabaseClient.js hands out, so every
 * `supabase.storage.from(<bucket>).<op>(...)` in the app -- the document
 * editor's download/upload, the folder pickers' list, the audio and
 * report flows -- is covered without touching its call site. Supabase
 * Storage stays the SOURCE OF TRUTH; RDS's `storage_objects` is a mirror
 * that is proven on every read before it is trusted.
 *
 * Per bucket flag `storage_<bucket>` (effective only while the bucket's
 * sync is fresh), behind the build switch REACT_APP_RDS_CUTOVER. Off = the
 * client is returned unwrapped, byte-for-byte today's behaviour.
 *
 *   download(path)   RDS's bytes and the LIVE object's eTag (one Supabase
 *                    list() of the parent folder) are fetched in parallel.
 *                    eTags equal -> RDS's bytes are served (`served: rds`,
 *                    `matched`). Different -> Supabase's bytes are served
 *                    and RDS is READ-REPAIRED with them (`mismatched`).
 *                    RDS lacks the object -> Supabase serves, RDS repaired
 *                    (`rds_missing`). Supabase cannot answer the eTag
 *                    lookup -> RDS's bytes are served `unverified` (the
 *                    Supabase download would have failed too). Any RDS
 *                    failure -> Supabase, reported. A stale mirror can
 *                    therefore cost a round trip, never stale content --
 *                    which is what lets a document editor sit on this.
 *   list(prefix)     both listings in parallel; served from RDS only when
 *                    the entry names agree (`matched`), else Supabase's
 *                    with the disagreement counted (`missing` = Supabase
 *                    has it, RDS does not; `extra` = the reverse).
 *   upload/update/remove/move/copy
 *                    Supabase first, exactly as today; when it succeeded
 *                    the same change is mirrored to RDS, best effort, not
 *                    awaited by the caller (`served: rds` when RDS took
 *                    it). The bucket sync reconciles anything missed.
 *                    (The Supabase call now starts after the cached mode
 *                    lookup, since independent mode may route it away.)
 *   createSignedUrl(s), getPublicUrl, info, exists, uploadToSignedUrl, ...
 *                    pass through untouched
 *                    (a URL the browser fetches itself has no RDS
 *                    equivalent yet -- Phase E.2 in the plan).
 *
 * INDEPENDENT source mode (§31): RDS owns its objects and nothing is
 * pulled from Supabase into it. Which buckets RDS owns comes from the
 * /cutover payload's `storage_buckets` (remembered across a /cutover
 * blip). A bucket NOT in that list behaves exactly as in mirror mode. A
 * bucket in it is "RDS-only". When RDS sends no `storage_buckets` (an
 * older RDS) ownership is unknown and the bucket keeps the pre-ownership
 * independent behaviour: reads as below, writes Supabase first + mirrored.
 * For an independent bucket the overlay runs whether or not the bucket's
 * flag is on (as the PHI overlay does), and:
 *   download         RDS's bytes, no eTag proof. RDS tombstoned the path
 *                    (the app deleted / moved it while independent) ->
 *                    Supabase-shaped "Object not found", Supabase NOT
 *                    asked. RDS never held it / fails -> Supabase's bytes
 *                    are returned to the caller but NEVER written into RDS
 *                    (no read-repair).
 *   list             RDS's listing, plus the Supabase entries RDS lacks
 *                    and has not tombstoned (objects only Supabase holds
 *                    stay visible; nothing is copied). Those entries must
 *                    also appear in an exact-key listV2 of the folder:
 *                    Supabase's list() matches case-insensitively with
 *                    `_` as a wildcard, so a sibling folder's objects
 *                    would otherwise show up and then fail to download.
 *   info             RDS's metadata for an object RDS holds (the editor's
 *                    size / not-found preflight), so it never judges a
 *                    document by Supabase's copy. Tombstoned -> "Object
 *                    not found". RDS lacks it / fails -> Supabase answers,
 *                    as download does.
 *   writes           RDS-only bucket: upload / update / remove / move /
 *                    copy go to RDS ONLY and Supabase gets nothing. The
 *                    answer is supabase-js shaped ({data, error}); an RDS
 *                    failure is {data: null, error} -- never a Supabase
 *                    write instead. upload without `upsert` refuses an
 *                    object RDS holds ("The resource already exists",
 *                    409 / "Duplicate"), update refuses one it does not
 *                    ("Object not found"); a body that cannot be re-read
 *                    (stream, FormData) is an error. A view-as /
 *                    read-only guard underneath still throws first (its
 *                    thrower is marked, see readOnlySupabaseProxy.js).
 *                    Ownership unknown: mirrored to RDS exactly as above.
 *   createSignedUrl(s)
 *                    RDS-only bucket: an object RDS holds is answered with
 *                    a blob: URL of RDS's bytes (revoked when the signed
 *                    URL would have expired) -- GET-able in this tab only:
 *                    not by HEAD, another device, the backend or an email.
 *                    Tombstoned -> "Object not found". RDS never held it /
 *                    fails, or `download` / `transform` options -> Supabase
 *                    signs, as before. Otherwise passthrough.
 *   getPublicUrl, uploadToSignedUrl, exists
 *                    always Supabase (an object written while RDS-only is
 *                    not there).
 *
 * Reports go to the same dashboard endpoint as every other cutover
 * (`pilot: storage_<bucket>`, `table: bucket:<name>`, `op`), never any
 * object content or path.
 *
 * No top-level import of rdsStorage / rdsCutover / rdsPhi: supabaseClient.js
 * imports this module, and those import supabaseClient.js -- requires are
 * deferred to call time, as in rdsPhiOverlay.js.
 */

const MIRRORED_WRITES = new Set(["upload", "update", "remove", "move", "copy"]);

/** The cutover flag / dashboard pilot for a bucket. */
export function storageFlagFor(bucket) {
  return `storage_${String(bucket).replace(/[^A-Za-z0-9]+/g, "_").toLowerCase()}`;
}

function deps() {
  // eslint-disable-next-line global-require
  const cutover = require("./rdsCutover");
  // eslint-disable-next-line global-require
  const storage = require("./rdsStorage");
  // The error classes come from rdsPhi, which rdsStorage throws them from:
  // neither module above re-exports them, and failureFields' instanceof
  // checks need the real ones.
  // eslint-disable-next-line global-require
  const phi = require("./rdsPhi");
  return {
    ...cutover, ...storage,
    PhiClientError: phi.PhiClientError,
    PhiClientUnavailable: phi.PhiClientUnavailable,
    getCutoverFlags: phi.getCutoverFlags,
  };
}

function splitPath(path) {
  const clean = String(path || "").replace(/^\/+/, "");
  const i = clean.lastIndexOf("/");
  return i < 0 ? { dir: "", base: clean } : { dir: clean.slice(0, i), base: clean.slice(i + 1) };
}

/**
 * Compare two list() answers by entry name. Pure; exported for tests.
 * Returns {equal, matched, missing, extra}: `missing` entries Supabase has
 * and RDS does not, `extra` the reverse.
 */
export function compareListings(rdsRows, supabaseRows) {
  const rds = new Set((rdsRows || []).map((r) => r && r.name).filter(Boolean));
  const sb = new Set((supabaseRows || []).map((r) => r && r.name).filter(Boolean));
  let matched = 0;
  let missing = 0;
  sb.forEach((n) => { if (rds.has(n)) matched += 1; else missing += 1; });
  let extra = 0;
  rds.forEach((n) => { if (!sb.has(n)) extra += 1; });
  return { equal: missing === 0 && extra === 0, matched, missing, extra };
}

/** Classify an RDS failure the way the other cutovers do; trips the shared
 * circuit on a timeout / unreachable. Returns report fields. */
function failureFields(err, d) {
  if (err instanceof d.PhiClientError) {
    return {
      outcome: err.statusCode === 404 ? "rds_missing" : "rds_error",
      reason: `PhiClientError ${err.statusCode}`, status_code: err.statusCode,
      detail: typeof err.detail === "string" ? err.detail.slice(0, 200) : "non-string detail omitted",
    };
  }
  if (err instanceof d.PhiClientUnavailable) {
    const msg = String(err.message || "");
    const reason = msg.includes("timed out") ? "timeout" : msg.includes("no active session") ? "no_session" : "unreachable";
    if (reason === "timeout" || reason === "unreachable") d.tripRdsCircuit();
    return { outcome: "rds_unavailable", reason };
  }
  return { outcome: "unexpected_error", reason: err?.name || "Error" };
}

// The most recent mirror / repair promise, so tests (and only tests) can
// wait for a fire-and-forget write to settle.
let lastBackground = Promise.resolve();
function background(promise) {
  lastBackground = promise.catch(() => {});
  return lastBackground;
}
/** Test hook -- resolves once the last background mirror write settled. */
export function _flushStorageMirrors() {
  return lastBackground;
}

/** A Blob for anything the Supabase upload API accepts that we can also
 * send to RDS; null for bodies we cannot re-read (streams, FormData). */
function toBlob(body, contentType) {
  if (typeof Blob !== "undefined" && body instanceof Blob) return body;
  if (typeof ArrayBuffer !== "undefined" && (body instanceof ArrayBuffer || ArrayBuffer.isView(body))) {
    return new Blob([body], { type: contentType || "application/octet-stream" });
  }
  if (typeof body === "string") return new Blob([body], { type: contentType || "text/plain" });
  return null;
}

/** Is RDS independent of Supabase right now? Never throws. */
async function independentMode(d) {
  try {
    return typeof d.isIndependent === "function" && (await d.isIndependent()) === true;
  } catch (_) {
    return false;
  }
}

// The last `storage_buckets` list RDS sent, kept so a /cutover blip does
// not flip an owned bucket's writes back to Supabase. null = unknown (no
// good payload yet, or an RDS that predates the field).
let knownStorageBuckets = null;

/** Test hook -- forget the remembered `storage_buckets`. */
export function _resetStorageOwnership() {
  knownStorageBuckets = null;
}

/**
 * Does RDS own `bucket` (its /cutover `storage_buckets`)? true / false, or
 * null when unknown -- the caller then keeps the pre-ownership behaviour
 * (reads from RDS, writes Supabase first + mirrored). Never throws.
 */
async function bucketOwnership(d, bucket) {
  try {
    const payload = typeof d.getCutoverFlags === "function" ? await d.getCutoverFlags() : null;
    if (payload && typeof payload === "object" && !payload.error) {
      knownStorageBuckets = Array.isArray(payload.storage_buckets)
        ? payload.storage_buckets.filter(Boolean).map(String)
        : null; // a good payload without the field: an older RDS
    }
  } catch (_) {
    // keep what was known
  }
  if (!knownStorageBuckets) return null;
  return knownStorageBuckets.includes(String(bucket));
}

/**
 * The overlay runs when the bucket's flag is effective, or always while
 * RDS is independent AND does not disown the bucket (RDS is the store
 * then). A bucket RDS says it does not own behaves exactly as in mirror
 * mode. Resolves {on, independent, rdsOnly}: `rdsOnly` = independent and
 * RDS OWNS the bucket, so writes go to RDS only.
 */
async function overlayState(d, bucket) {
  if (await independentMode(d)) {
    const owned = await bucketOwnership(d, bucket);
    if (owned !== false) return { on: true, independent: true, rdsOnly: owned === true };
  }
  try {
    return { on: (await d.isCutoverEffective(storageFlagFor(bucket))) === true, independent: false, rdsOnly: false };
  } catch (_) {
    return { on: false, independent: false, rdsOnly: false };
  }
}

// ---- supabase-js shaped errors ---------------------------------------------

/** Shaped like storage-js's StorageApiError (name, message, status,
 * statusCode, __isStorageError), so callers' existing checks -- message
 * "Object not found" / "already exists", statusCode 400/404/409 -- work. */
class RdsStorageError extends Error {
  constructor(message, { status, statusCode, error, name = "StorageApiError" } = {}) {
    super(message);
    this.name = name;
    this.__isStorageError = true;
    if (status !== undefined) this.status = status;
    if (statusCode !== undefined) this.statusCode = statusCode;
    if (error !== undefined) this.error = error;
  }

  toJSON() {
    return { name: this.name, message: this.message, status: this.status, statusCode: this.statusCode };
  }
}

/** Supabase's answer for a missing object: HTTP 400, body statusCode "404". */
function notFoundError() {
  return new RdsStorageError("Object not found", { status: 400, statusCode: "404", error: "not_found" });
}

/** Supabase's answer for upload() onto an existing object without upsert. */
function duplicateError() {
  return new RdsStorageError("The resource already exists", { status: 409, statusCode: "409", error: "Duplicate" });
}

/** An RDS failure as a storage error (never a silent Supabase write). */
function rdsFailureError(err, d) {
  if (err instanceof RdsStorageError) return err;
  if (err instanceof d.PhiClientError) {
    if (err.statusCode === 404) return notFoundError();
    if (err.statusCode === 409) return duplicateError();
    return new RdsStorageError(`RDS storage error ${err.statusCode}`, {
      status: err.statusCode, statusCode: String(err.statusCode),
    });
  }
  if (err instanceof d.PhiClientUnavailable) {
    return new RdsStorageError(`RDS storage unavailable: ${String(err.message || "").slice(0, 100)}`, {
      name: "StorageUnknownError",
    });
  }
  return new RdsStorageError(`RDS storage error: ${err?.message || err?.name || "Error"}`.slice(0, 200), {
    name: "StorageUnknownError",
  });
}

function isTombstone(err, d) {
  return !!(err && err instanceof d.PhiClientError && err.statusCode === 404 && err.tombstone === true);
}

/** storage-js's _removeEmptyFolders: no leading/trailing slash, no "//". */
function cleanStoragePath(path) {
  return String(path || "").replace(/^\/|\/$/g, "").replace(/\/+/g, "/");
}

/** The tombstoned names of an RDS listing, relative to `prefix`. */
function goneNames(d, tombstones, prefix) {
  return typeof d.tombstoneNames === "function" ? d.tombstoneNames(tombstones, prefix || "") : new Set();
}

const EXACT_LIST_MAX_PAGES = 10;

/**
 * The entry names Supabase holds DIRECTLY under `prefix`, by exact key.
 * Supabase's list() matches the prefix with ILIKE: case-insensitive, and
 * `_` / `%` are wildcards, so listing ".../Team_ENgg Team/11-02-26" also
 * returns ".../Team_Engg Team/11-02-26/*" -- entries no path built from the
 * listed folder can download or sign ("Object not found"). listV2 compares
 * the key exactly. Resolves a Set of names, or null when it cannot say (no
 * listV2, an error, a folder too large to page through) -- the caller then
 * keeps list()'s entries rather than hide a real file.
 */
async function exactSupabaseNames(api, prefix) {
  if (typeof api.listV2 !== "function") return null;
  const clean = cleanStoragePath(prefix);
  const base = clean ? `${clean}/` : "";
  const nameOf = (entry) => {
    const key = String((entry && (entry.key || entry.name)) || "").replace(/\/+$/, "");
    return key.startsWith(base) ? key.slice(base.length) : key.slice(key.lastIndexOf("/") + 1);
  };
  const names = new Set();
  let cursor;
  try {
    for (let page = 0; page < EXACT_LIST_MAX_PAGES; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const { data, error } = await api.listV2({
        prefix: base, with_delimiter: true, limit: 1000, ...(cursor ? { cursor } : {}),
      });
      if (error || !data) return null;
      (data.objects || []).concat(data.folders || []).forEach((e) => {
        const n = nameOf(e);
        if (n) names.add(n);
      });
      if (!data.hasNext) return names;
      cursor = data.nextCursor;
      if (!cursor) return null;
    }
  } catch (_) {
    return null;
  }
  return null;
}

/** The live object's eTag via one list() of its folder. Resolves to
 * {found, etag}; rejects when Supabase could not answer. */
async function liveEtag(api, path) {
  const { dir, base } = splitPath(path);
  const { data, error } = await api.list(dir, { search: base, limit: 100 });
  if (error) throw error;
  const hit = (data || []).find((e) => e && e.name === base);
  if (!hit) return { found: false, etag: null };
  // eslint-disable-next-line global-require
  const { normalizeEtag } = require("./rdsStorage");
  return { found: true, etag: normalizeEtag(hit.metadata?.eTag) };
}

async function overlayDownload(api, bucket, schema, path, opts) {
  const passthrough = () => api.download(path, opts);
  if (opts && opts.transform) return passthrough();
  let d;
  let mode;
  try {
    d = deps();
    mode = await overlayState(d, bucket);
    if (!mode.on) return passthrough();
  } catch (_) {
    return passthrough();
  }
  const base = { pilot: storageFlagFor(bucket), tenant: schema, table: `bucket:${bucket}`, op: "download", sample_size: 1 };
  const report = (fields) => d.reportCutoverOutcome({ ...base, ...fields });
  if (d.rdsCircuitOpen()) {
    report({ served: "supabase_fallback", outcome: "skipped", reason: "circuit_open" });
    return passthrough();
  }
  if (mode.independent) {
    // RDS is the store: serve it; Supabase only answers what RDS lacks,
    // and its bytes are never copied into RDS.
    try {
      const rds = await d.getObject(bucket, path);
      report({ served: "rds", outcome: "ok", reason: "independent" });
      return { data: rds.blob, error: null };
    } catch (err) {
      if (isTombstone(err, d)) {
        // Deleted / moved by the app while independent: gone, whatever
        // Supabase still holds.
        report({ served: "rds", outcome: "tombstoned", reason: "independent" });
        return { data: null, error: notFoundError() };
      }
      report({ served: "supabase_fallback", ...failureFields(err, d), detail: "not_repaired: independent" });
      return passthrough();
    }
  }
  const [rdsRes, liveRes] = await Promise.allSettled([d.getObject(bucket, path), liveEtag(api, path)]);
  const live = liveRes.status === "fulfilled" ? liveRes.value : null;

  // Supabase serves; RDS is brought up to date with what Supabase served.
  const fallback = async (fields) => {
    const result = await passthrough();
    let repair = "not_repaired";
    // No point writing to an RDS that just timed out or blew up.
    const repairable = live && live.found && fields.outcome !== "rds_unavailable" && fields.outcome !== "unexpected_error";
    if (result && !result.error && result.data && repairable) {
      const blob = toBlob(result.data);
      if (blob) {
        repair = "repaired";
        background(
          d.putObject(bucket, path, blob, { contentType: blob.type || undefined, sourceEtag: live.etag })
            .catch(() => report({ ...base, op: "repair", served: "supabase_fallback", outcome: "repair_failed" })),
        );
      }
    }
    // `detail` says whether RDS was brought up to date; RDS's own error
    // text only when nothing was repaired.
    report({ served: "supabase_fallback", ...fields, detail: repair === "repaired" ? repair : (fields.detail || repair) });
    return result;
  };

  if (rdsRes.status === "rejected") return fallback(failureFields(rdsRes.reason, d));
  const rds = rdsRes.value;
  if (!live) {
    // Supabase could not even answer the eTag lookup: RDS is the only copy
    // that can answer right now.
    report({ served: "rds", outcome: "unverified", reason: liveRes.reason?.name || "supabase_list_error" });
    return { data: rds.blob, error: null };
  }
  if (!live.found) {
    // Supabase no longer has it; the app must see Supabase's answer, not a
    // ghost from the mirror (the next sync prunes it).
    report({ served: "supabase_fallback", outcome: "rds_extra", extra: 1, reason: "not_in_supabase" });
    return passthrough();
  }
  if (live.etag && rds.etag && live.etag === rds.etag) {
    report({ served: "rds", outcome: "ok", matched: 1 });
    return { data: rds.blob, error: null };
  }
  return fallback({ outcome: "mismatched", mismatched: 1, reason: "etag_mismatch" });
}

async function overlayList(api, bucket, schema, prefix, opts, params) {
  const passthrough = () => api.list(prefix, opts, params);
  let d;
  let mode;
  try {
    d = deps();
    mode = await overlayState(d, bucket);
    if (!mode.on) return passthrough();
  } catch (_) {
    return passthrough();
  }
  const base = { pilot: storageFlagFor(bucket), tenant: schema, table: `bucket:${bucket}`, op: "list" };
  const report = (fields) => d.reportCutoverOutcome({ ...base, ...fields });
  if (d.rdsCircuitOpen()) {
    report({ served: "supabase_fallback", outcome: "skipped", reason: "circuit_open" });
    return passthrough();
  }
  const [rdsRes, sbRes] = await Promise.allSettled([d.listObjects(bucket, prefix || "", opts || {}), passthrough()]);
  const sb = sbRes.status === "fulfilled" ? sbRes.value : { data: null, error: sbRes.reason };
  if (rdsRes.status === "rejected") {
    report({ served: "supabase_fallback", sample_size: (sb.data || []).length, ...failureFields(rdsRes.reason, d) });
    return sb;
  }
  const rows = rdsRes.value.rows;
  if (!sb || sb.error || !Array.isArray(sb.data)) {
    report({ served: "rds", outcome: "unverified", sample_size: rows.length, reason: sb?.error?.name || "supabase_list_error" });
    return { data: rows, error: null };
  }
  const cmp = compareListings(rows, sb.data);
  if (mode.independent) {
    // RDS's listing, plus whatever only Supabase holds (read, never copied).
    // Names the app deleted / moved while independent stay gone.
    const have = new Set(rows.map((r) => r && r.name).filter(Boolean));
    const gone = goneNames(d, rdsRes.value.tombstones, prefix);
    const notInRds = sb.data.filter((r) => r && r.name && !have.has(r.name));
    const notGone = notInRds.filter((r) => !gone.has(r.name));
    // Only entries really in THIS folder (see exactSupabaseNames).
    const exact = notGone.length ? await exactSupabaseNames(api, prefix) : null;
    const onlySupabase = exact ? notGone.filter((r) => exact.has(r.name)) : notGone;
    const ghosts = notGone.length - onlySupabase.length;
    const hidden = notInRds.length - notGone.length;
    report({
      served: "rds", outcome: cmp.equal ? "ok" : "merged", sample_size: rows.length + onlySupabase.length,
      matched: cmp.matched, missing: cmp.missing - hidden - ghosts, extra: cmp.extra, reason: "independent",
      ...(hidden ? { masked: hidden } : {}),
      ...(ghosts ? { detail: `dropped ${ghosts} supabase entr${ghosts === 1 ? "y" : "ies"} from another folder` } : {}),
    });
    return { data: rows.concat(onlySupabase), error: null };
  }
  if (cmp.equal) {
    report({ served: "rds", outcome: "ok", sample_size: sb.data.length, matched: cmp.matched });
    return { data: rows, error: null };
  }
  report({
    served: "supabase_fallback", outcome: "mismatched", sample_size: sb.data.length,
    matched: cmp.matched, missing: cmp.missing, extra: cmp.extra, reason: "listing_mismatch",
  });
  return sb;
}

/** info() while independent: RDS's metadata, shaped like supabase-js's
 * camelised info() answer. Not independent -> Supabase, untouched. */
async function overlayInfo(api, bucket, schema, path) {
  const passthrough = () => api.info(path);
  let d;
  try {
    d = deps();
    if (!(await overlayState(d, bucket)).independent || d.rdsCircuitOpen()) return passthrough();
  } catch (_) {
    return passthrough();
  }
  const report = (fields) => d.reportCutoverOutcome({
    pilot: storageFlagFor(bucket), tenant: schema, table: `bucket:${bucket}`, op: "info", sample_size: 1, ...fields,
  });
  const { dir, base } = splitPath(path);
  let hit;
  let tombstoned = false;
  try {
    const { rows, tombstones } = await d.listObjects(bucket, dir, { search: base, limit: 1000 });
    hit = (rows || []).find((r) => r && r.name === base && r.id);
    tombstoned = !hit && goneNames(d, tombstones, dir).has(base);
  } catch (err) {
    report({ served: "supabase_fallback", ...failureFields(err, d) });
    return passthrough();
  }
  if (tombstoned) {
    report({ served: "rds", outcome: "tombstoned", reason: "independent" });
    return { data: null, error: notFoundError() };
  }
  if (!hit) {
    report({ served: "supabase_fallback", outcome: "rds_missing", reason: "independent" });
    return passthrough();
  }
  report({ served: "rds", outcome: "ok", reason: "independent" });
  const meta = hit.metadata || {};
  return {
    data: {
      id: hit.id,
      name: path,
      size: meta.size,
      contentType: meta.mimetype,
      etag: meta.eTag,
      lastModified: meta.lastModified || hit.updated_at,
      createdAt: hit.created_at,
      metadata: meta,
    },
    error: null,
  };
}

/** Mirror one succeeded Supabase write to RDS. Never throws; reports. */
async function mirrorWrite(bucket, schema, op, run) {
  let d;
  try {
    d = deps();
    if (!(await overlayState(d, bucket)).on) return { served: "supabase_only", reason: "flag_off" };
  } catch (_) {
    return { served: "supabase_only", reason: "flag_off" };
  }
  const base = { pilot: storageFlagFor(bucket), tenant: schema, table: `bucket:${bucket}`, op, sample_size: 1 };
  const report = (fields) => d.reportCutoverOutcome({ ...base, ...fields });
  if (d.rdsCircuitOpen()) {
    report({ served: "supabase_fallback", outcome: "skipped", reason: "circuit_open" });
    return { served: "supabase_fallback", reason: "circuit_open" };
  }
  try {
    const skipped = await run(d);
    if (skipped) {
      report({ served: "supabase_only", outcome: "skipped", reason: skipped });
      return { served: "supabase_only", reason: skipped };
    }
  } catch (err) {
    const fields = failureFields(err, d);
    report({ served: "supabase_fallback", ...fields });
    return { served: "supabase_fallback", reason: fields.reason };
  }
  report({ served: "rds", outcome: "ok" });
  return { served: "rds", reason: null };
}

// ---- RDS-only writes (independent + RDS owns the bucket) -------------------

// Set by the view-as / read-only guards underneath on the thrower they hand
// out for a blocked write (readOnlySupabaseProxy.markBlockedStorageWrite).
const BLOCKED_STORAGE_WRITE = Symbol.for("notesai.storage.blockedWrite");

/** PUT for upload()/update() with Supabase's existence rules: upload
 * without upsert refuses an object RDS already holds ("Duplicate"), update
 * refuses one it does not ("Object not found"). Resolves supabase-js's
 * {path, id?, fullPath}. */
async function putRdsOnly(d, bucket, rawPath, body, opts, { mustExist }) {
  const path = cleanStoragePath(rawPath);
  if (!path) throw new RdsStorageError("A path is required", { status: 400, statusCode: "400", error: "invalid_path" });
  const blob = toBlob(body, opts?.contentType);
  if (!blob) {
    // Streams / FormData cannot be re-read here; never fall back to Supabase.
    throw new RdsStorageError("This upload body type cannot be stored while RDS holds the bucket", {
      name: "StorageUnknownError", error: "unsupported_body",
    });
  }
  if (mustExist || opts?.upsert !== true) {
    const { exists } = await d.objectExists(bucket, path);
    if (mustExist && !exists) throw notFoundError();
    if (!mustExist && exists) throw duplicateError();
  }
  const res = await d.putObject(bucket, path, blob, { contentType: opts?.contentType || blob.type || undefined });
  const data = { path, fullPath: `${bucket}/${path}` };
  const id = res && (res.id || res.object_id);
  if (id) data.id = id;
  return data;
}

function sameBucket(bucket, opts) {
  if (opts && opts.destinationBucket && opts.destinationBucket !== bucket) {
    throw new RdsStorageError("Cross-bucket move/copy is not supported while RDS holds the bucket", {
      name: "StorageUnknownError", error: "cross_bucket",
    });
  }
}

const RDS_ONLY_WRITES = {
  upload: (d, bucket, path, body, opts) => putRdsOnly(d, bucket, path, body, opts, { mustExist: false }),
  update: (d, bucket, path, body, opts) => putRdsOnly(d, bucket, path, body, opts, { mustExist: true }),
  async remove(d, bucket, paths) {
    const clean = (Array.isArray(paths) ? paths : []).map(cleanStoragePath).filter(Boolean);
    if (!clean.length) return [];
    const res = await d.removeObjects(bucket, clean);
    // supabase-js answers the removed objects (name = full path).
    const removed = Array.isArray(res?.removed) ? res.removed : clean;
    return removed.map((r) => (r && typeof r === "object"
      ? { bucket_id: bucket, ...r, name: r.name || r.path }
      : { bucket_id: bucket, name: String(r) }));
  },
  async move(d, bucket, from, to, opts) {
    sameBucket(bucket, opts);
    await d.moveObject(bucket, cleanStoragePath(from), cleanStoragePath(to));
    return { message: "Successfully moved" };
  },
  async copy(d, bucket, from, to, opts) {
    sameBucket(bucket, opts);
    const dest = cleanStoragePath(to);
    await d.copyObject(bucket, cleanStoragePath(from), dest);
    return { path: `${bucket}/${dest}` };
  },
};

/** Run one write against RDS only. Resolves supabase-js's {data, error};
 * a failure is {data: null, error} -- Supabase is never written instead. */
async function rdsOnlyWrite(d, bucket, schema, op, args) {
  const report = (fields) => d.reportCutoverOutcome({
    pilot: storageFlagFor(bucket), tenant: schema, table: `bucket:${bucket}`, op, sample_size: 1, ...fields,
  });
  try {
    const data = await RDS_ONLY_WRITES[op](d, bucket, ...args);
    report({ served: "rds", outcome: "ok", reason: "rds_only" });
    return { data, error: null };
  } catch (err) {
    const fields = err instanceof RdsStorageError
      ? { outcome: "refused", reason: err.error || err.name }
      : failureFields(err, d);
    report({ served: "none", ...fields, detail: "rds_only: not written to supabase" });
    return { data: null, error: rdsFailureError(err, d) };
  }
}

/** What to mirror to RDS after Supabase accepted a write (mirror mode, or
 * independent with the bucket's ownership unknown). */
function mirrorRun(op, bucket, args) {
  if (op === "upload" || op === "update") {
    const [path, body, opts] = args;
    return (d) => {
      const blob = toBlob(body, opts?.contentType);
      if (!blob) return "unsupported_body";
      return d.putObject(bucket, path, blob, { contentType: opts?.contentType || blob.type || undefined })
        .then(() => null);
    };
  }
  if (op === "remove") return (d) => d.removeObjects(bucket, args[0]).then(() => null);
  const fn = op === "move" ? "moveObject" : "copyObject";
  return (d) => d[fn](bucket, args[0], args[1]).then(() => null);
}

function writeHandler(target, bucket, schema, op) {
  return (...args) => {
    const method = target[op];
    // A guard underneath (view-as / read-only role) handed out a thrower:
    // call it now so it throws to the caller exactly as before -- RDS is
    // never asked to take a write the app may not make.
    if (typeof method !== "function" || method[BLOCKED_STORAGE_WRITE] === true) return target[op](...args);
    return (async () => {
      let d;
      let state = { rdsOnly: false };
      try {
        d = deps();
        state = await overlayState(d, bucket);
      } catch (_) {
        state = { rdsOnly: false };
      }
      if (state.rdsOnly) return rdsOnlyWrite(d, bucket, schema, op, args);
      // Supabase first, then mirrored to RDS (best effort, not awaited).
      const result = await method.apply(target, args);
      if (result && !result.error) background(mirrorWrite(bucket, schema, op, mirrorRun(op, bucket, args)));
      return result;
    })();
  };
}

// ---- signed URLs while RDS owns the bucket ----------------------------------

const MAX_TIMER_MS = 2147483647;

/** A blob: URL for RDS's copy, revoked when a signed URL would expire. */
function blobUrlFor(blob, expiresIn) {
  const url = URL.createObjectURL(blob);
  const ms = Math.min(Math.max(Number(expiresIn) || 0, 60) * 1000, MAX_TIMER_MS);
  setTimeout(() => {
    try { URL.revokeObjectURL(url); } catch (_) { /* already gone */ }
  }, ms);
  return url;
}

/** {url} from RDS, {gone} for a tombstone, {miss: reportFields} otherwise. */
async function signFromRds(d, bucket, path, expiresIn) {
  try {
    const rds = await d.getObject(bucket, cleanStoragePath(path));
    return { url: blobUrlFor(rds.blob, expiresIn) };
  } catch (err) {
    if (isTombstone(err, d)) return { gone: true };
    return { miss: failureFields(err, d) };
  }
}

/** Should createSignedUrl(s) answer from RDS? Only independent + owned
 * bucket, a browser that can mint blob: URLs, and no download/transform
 * option (a blob: URL can neither force a download name nor resize). */
async function signingState(bucket, opts) {
  if (opts && (opts.download || opts.transform)) return null;
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return null;
  try {
    const d = deps();
    if (!(await overlayState(d, bucket)).rdsOnly || d.rdsCircuitOpen()) return null;
    return d;
  } catch (_) {
    return null;
  }
}

async function overlaySignedUrl(api, bucket, schema, path, expiresIn, opts) {
  const passthrough = () => api.createSignedUrl(path, expiresIn, opts);
  const d = await signingState(bucket, opts);
  if (!d) return passthrough();
  const report = (fields) => d.reportCutoverOutcome({
    pilot: storageFlagFor(bucket), tenant: schema, table: `bucket:${bucket}`, op: "sign", sample_size: 1, ...fields,
  });
  const one = await signFromRds(d, bucket, path, expiresIn);
  if (one.url) {
    report({ served: "rds", outcome: "ok", reason: "rds_only" });
    return { data: { signedUrl: one.url }, error: null };
  }
  if (one.gone) {
    report({ served: "rds", outcome: "tombstoned", reason: "rds_only" });
    return { data: null, error: notFoundError() };
  }
  // RDS never held it (an object only Supabase has) or could not answer.
  report({ served: "supabase_fallback", ...one.miss, detail: "rds_only: signed by supabase" });
  return passthrough();
}

async function overlaySignedUrls(api, bucket, schema, paths, expiresIn, opts) {
  const passthrough = () => api.createSignedUrls(paths, expiresIn, opts);
  if (!Array.isArray(paths) || !paths.length) return passthrough();
  const d = await signingState(bucket, opts);
  if (!d) return passthrough();
  const results = await Promise.all(paths.map((p) => signFromRds(d, bucket, p, expiresIn)));
  const misses = paths.filter((_, i) => results[i].miss);
  const fromSupabase = new Map();
  let sbError = null;
  if (misses.length) {
    const sb = await api.createSignedUrls(misses, expiresIn, opts);
    if (sb?.error) sbError = sb.error;
    else (sb?.data || []).forEach((e) => { if (e && e.path) fromSupabase.set(e.path, e); });
  }
  const served = results.filter((r) => r.url).length;
  const gone = results.filter((r) => r.gone).length;
  d.reportCutoverOutcome({
    pilot: storageFlagFor(bucket), tenant: schema, table: `bucket:${bucket}`, op: "sign",
    served: misses.length ? "supabase_fallback" : "rds", outcome: misses.length ? "merged" : "ok",
    reason: "rds_only", sample_size: paths.length, matched: served, missing: misses.length, ...(gone ? { masked: gone } : {}),
  });
  if (sbError && !served && !gone) return { data: null, error: sbError };
  const missing = (path, message) => ({ error: message, path, signedURL: null, signedUrl: null });
  return {
    data: paths.map((p, i) => {
      const r = results[i];
      if (r.url) return { error: null, path: p, signedURL: r.url, signedUrl: r.url };
      if (r.gone) return missing(p, "Object not found");
      return fromSupabase.get(p) || missing(p, sbError?.message || "Object not found");
    }),
    error: null,
  };
}

function wrapBucket(api, bucket, schema) {
  return new Proxy(api, {
    get(target, key) {
      if (key === "download") return (path, opts) => overlayDownload(target, bucket, schema, path, opts);
      if (key === "list") return (prefix, opts, params) => overlayList(target, bucket, schema, prefix, opts, params);
      if (key === "info" && typeof target.info === "function") return (path) => overlayInfo(target, bucket, schema, path);
      if (key === "createSignedUrl" && typeof target.createSignedUrl === "function") {
        return (path, expiresIn, opts) => overlaySignedUrl(target, bucket, schema, path, expiresIn, opts);
      }
      if (key === "createSignedUrls" && typeof target.createSignedUrls === "function") {
        return (paths, expiresIn, opts) => overlaySignedUrls(target, bucket, schema, paths, expiresIn, opts);
      }
      // Writes: a guard underneath that THROWS (view-as / read-only role)
      // still throws synchronously to the caller (see writeHandler).
      if (MIRRORED_WRITES.has(key)) return writeHandler(target, bucket, schema, key);
      const value = Reflect.get(target, key);
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

// The explicit name wins (the caller knows which tenant it built the
// client for); the client's own schemaName is the fallback.
function schemaOf(client, explicit) {
  return explicit || client?.rest?.schemaName || client?.schemaName || "public";
}

/**
 * Wrap a Supabase client so every `storage.from(bucket)` gets the overlay
 * (see the module comment). Returns the client itself when the build
 * switch is off, or when the client has no storage API (a schema-scoped
 * PostgrestClient).
 */
export function wrapSupabaseClientForStorageOverlay(client, schemaName) {
  if (!client) return client;
  let enabled = false;
  try {
    // eslint-disable-next-line global-require
    enabled = require("./rdsCutover").cutoverBuildEnabled();
  } catch (_) {
    enabled = false;
  }
  if (!enabled) return client;
  const schema = schemaOf(client, schemaName);
  return new Proxy(client, {
    get(target, prop) {
      if (prop === "schema") {
        return (name) => wrapSupabaseClientForStorageOverlay(target.schema(name), name);
      }
      if (prop === "storage") {
        const s = target.storage;
        if (!s) return s;
        return new Proxy(s, {
          get(st, k) {
            if (k === "from") return (bucket) => wrapBucket(st.from(bucket), bucket, schema);
            const v = Reflect.get(st, k);
            return typeof v === "function" ? v.bind(st) : v;
          },
        });
      }
      return Reflect.get(target, prop);
    },
  });
}

export { MIRRORED_WRITES as _MIRRORED_WRITES };
